import { isEmpty } from '@intelligence/v3/utils/common';

import type { IFilterControlFieldMap } from '../controlModule';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';

export interface IFilterFieldMapMiddlewareParams {
  defaultFieldMap?: IFilterControlFieldMap;
}

const RANGE_API_FIELD_FALLBACK_MAP: IRecord<{
  minField: string;
  maxField: string;
}> = {
  revenueRange: {
    minField: 'revenue_min',
    maxField: 'revenue_max',
  },
  arpuRange: {
    minField: 'arpu_min',
    maxField: 'arpu_max',
  },
  activeUsersRange: {
    minField: 'au_min',
    maxField: 'au_max',
  },
  downloadsRange: {
    minField: 'downloads_min',
    maxField: 'downloads_max',
  },
};

const RANGE_URL_FIELD_FALLBACK_MAP: IRecord<{
  minField: string;
  maxField: string;
}> = {
  revenueRange: {
    minField: 'revenueRangeMin',
    maxField: 'revenueRangeMax',
  },
  arpuRange: {
    minField: 'arpuRangeMin',
    maxField: 'arpuRangeMax',
  },
  activeUsersRange: {
    minField: 'activeUsersRangeMin',
    maxField: 'activeUsersRangeMax',
  },
  downloadsRange: {
    minField: 'downloadsRangeMin',
    maxField: 'downloadsRangeMax',
  },
};

const API_FIELD_FALLBACK_MAP_BY_TYPE: IRecord<string> = {
  company_select: 'companies',
  data_source: 'data_sources',
  platform: 'platforms',
};

const URL_FIELD_FALLBACK_MAP_BY_TYPE: IRecord<string> = {
  company_select: 'company',
  data_source: 'dataSource',
  platform: 'platform',
};

const getDefaultFieldMap = (params?: unknown): IFilterControlFieldMap => {
  const fieldMapParams = params as IFilterFieldMapMiddlewareParams | undefined;

  return fieldMapParams?.defaultFieldMap || {};
};

export const getDefaultApiFieldMap = (params?: unknown): IRecord<string> => {
  return getDefaultFieldMap(params).api || {};
};

export const getDefaultUrlFieldMap = (params?: unknown): IRecord<string> => {
  return getDefaultFieldMap(params).url || {};
};

export const getItemApiParamsFieldMap = (item: IFilterItemConfig, params?: unknown): IRecord<string> => {
  const fieldMap = item?.apiParamsFieldMapResolver?.({ item, params });
  return fieldMap || {};
};

export const getItemUrlParamsFieldMap = (item: IFilterItemConfig, params?: unknown): IRecord<string> => {
  const fieldMap = item?.urlParamsFieldMapResolver?.({ item, params });
  return fieldMap || {};
};

export const resolveApiField = (item: IFilterItemConfig, fallbackField?: string, params?: unknown): string => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return (
    apiParamsFieldMap.value ||
    defaultApiFieldMap.value ||
    fallbackField ||
    API_FIELD_FALLBACK_MAP_BY_TYPE[item?.type] ||
    item?.field ||
    ''
  );
};

export const resolveUrlField = (item: IFilterItemConfig, fallbackField?: string, params?: unknown): string => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return (
    urlParamsFieldMap.value ||
    defaultUrlFieldMap.value ||
    fallbackField ||
    URL_FIELD_FALLBACK_MAP_BY_TYPE[item?.type] ||
    item?.field ||
    ''
  );
};

export const resolveRangeApiFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  minField: string;
  maxField: string;
} => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);
  const field = item?.field || '';
  const fallbackFieldMap = RANGE_API_FIELD_FALLBACK_MAP[field];

  return {
    minField: apiParamsFieldMap?.min || defaultApiFieldMap.min || fallbackFieldMap?.minField || `${field}_min`,
    maxField: apiParamsFieldMap?.max || defaultApiFieldMap.max || fallbackFieldMap?.maxField || `${field}_max`,
  };
};

export const resolveRangeUrlFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  minField: string;
  maxField: string;
} => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);
  const field = item?.field || '';
  const fallbackFieldMap = RANGE_URL_FIELD_FALLBACK_MAP[field];

  return {
    minField: urlParamsFieldMap?.min || defaultUrlFieldMap.min || fallbackFieldMap?.minField || `${field}Min`,
    maxField: urlParamsFieldMap?.max || defaultUrlFieldMap.max || fallbackFieldMap?.maxField || `${field}Max`,
  };
};

export const hasFieldName = (field?: string | null): field is string => {
  return !isEmpty(field);
};
